import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchStateEmployeeFields, getStateEmployeeSections } from "store/stateEmployeeSlice";
import { getLang, getRoles } from "store/userSlice";
import { useTranslation } from "translation/useTranslation";
import EditorInput from "components/ui/EditorInput";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { en, de } from 'yup-locales';
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { empty, errorMessage, formatNumberTo, getTextFromHtml, isTrue, removeTrailingZeros } from "helpers/common";
import { api } from "helpers/axios";
import * as Sentry from "@sentry/react";
import { toast } from "helpers/toast";
import ErrorAlert from "components/ui/ErrorAlert";
import sanitize from "helpers/sanitize";
import Creatable from "react-select/creatable";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { fetchRegularEvaluationLevels, getRegularEvaluationLevels } from "store/balanceScorecardSlice";
import { getGeneralSettings } from "store/generalSettingsSlice";
import { getAllCustomNames, getAllFields } from "store/fieldsSlice";
import { selectGlobalPayfactorValues } from "store/payfactorSlice";
import { Button, DateInput, Input, NativeSelect, Select } from "components/ui";

dayjs.extend(customParseFormat);

const STATE_PAYFACTOR_FIELDS = ["pay_grade", "job_differs_from_classification"];
const optionValuesEqual = (left, right, ignoreCase = false) => {
  if (!ignoreCase) return left === right;
  const leftValue = String(left ?? '').trim();
  const rightValue = String(right ?? '').trim();
  return leftValue.toLocaleLowerCase() === rightValue.toLocaleLowerCase();
};

function StateEmployeeInputTag({ field, type, form, regularEvaluationLevels = [], readOnly = false, booleanAsSelect = false }) {
  const { translate, editableTranslate } = useTranslation();
  const dispatch = useDispatch();

  const [values, setValues] = useState([]);

  const { register, watch, setValue, errors, control } = form;

  const watchValue = watch(field.identifier);
  const statePayfactorValues = useSelector(selectGlobalPayfactorValues);
  const usesStatePayfactorGrades = STATE_PAYFACTOR_FIELDS.includes(field?.identifier);
  const isCreatable = field?.is_createable && !usesStatePayfactorGrades;

  const payfactorGradeOptions = useMemo(() => {
    if (!usesStatePayfactorGrades) return [];

    const map = new Map();

    (statePayfactorValues || []).forEach((row) => {
      if (!row?.grade || map.has(row.grade)) return;

      map.set(row.grade, {
        value: row.grade,
        label: row.grade_label || row.grade,
      });
    });

    return Array.from(map.values());
  }, [statePayfactorValues, usesStatePayfactorGrades]);

  useEffect(() => {
    if (!usesStatePayfactorGrades && (isCreatable || field?.dynamic_values)) fetchValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchValues = async () => {
    try {
      let identifier = field.identifier;

      const { data, status } = await api.get(`/settings/fields/${identifier}/values`);
      if (status === 200) {
        // backend returns { data: [...] }
        setValues(data.data || []);
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const normalizeFetchedOptions = (raw) => {
    if (!Array.isArray(raw)) return [];

    if (raw.length > 0 && typeof raw[0] === "string") {
      return raw.map((v) => ({ value: v, label: v }));
    }

    if (raw.length > 0 && typeof raw[0] === "object") {
      return raw
        .filter((o) => o && o.value !== undefined && o.value !== null)
        .map((o) => ({
          value: o.value,
          label: o.label ?? String(o.value),
          group: o.group,
          label_translation_key: o.label_translation_key,
        }));
    }

    return [];
  };

  const fetchedOptions = useMemo(() => normalizeFetchedOptions(values), [values]);

  const effectiveOptions = useMemo(() => {
    if (field?.identifier === 'previous_evaluation_results' && regularEvaluationLevels?.length > 0) {
      return regularEvaluationLevels.filter(l => l.description !== 'sub').map(lvl => ({
        label: lvl.level,
        value: lvl.value
      }));
    }

    if (usesStatePayfactorGrades) return payfactorGradeOptions;
    if (field?.dynamic_values) {
      const seen = new Set();
      return [...(field?.options || []), ...fetchedOptions].filter((option) => {
        const optionValue = String(option?.value ?? '').trim();
        const normalized = field?.identifier === 'gender' ? optionValue.toLocaleLowerCase() : optionValue;
        if (!normalized || seen.has(normalized)) return false;
        seen.add(normalized);
        return true;
      });
    }
    return field?.options || [];
  }, [field?.dynamic_values, fetchedOptions, field?.options, field?.identifier, payfactorGradeOptions, regularEvaluationLevels, usesStatePayfactorGrades]);

  const selectOptions = useMemo(() => {
    const arr = [];

    const hasOptions = (type === "select") || (effectiveOptions && effectiveOptions.length > 0) || isCreatable;

    if (hasOptions) {
      if (field?.grouped_options) {
        let curValueExist = false;

        const groups = {};

        for (const opt of effectiveOptions) {
          if (opt?.group) {
            if (!groups[opt.group]) groups[opt.group] = [];

            groups[opt.group].push({
              value: opt?.value,
              label: opt?.label_translation_key
                ? translate(opt?.label_translation_key, opt?.value)
                : (opt?.label ?? String(opt?.value)),
            });

            if (optionValuesEqual(opt?.value, watchValue, field?.identifier === 'gender')) curValueExist = true;
          }
        }

        Object.keys(groups).forEach((key) => {
          arr.push({
            label: key,
            options: groups[key],
          });
        });

        const customValues = [];

        // For createable: add custom values (strings)
        if (isCreatable && values.length > 0) {
          values.forEach((val) => {
            const exist = effectiveOptions.find((opt) => optionValuesEqual(opt?.value, val, field?.identifier === 'gender'));
            if (!exist) {
              customValues.push({ value: val, label: val });
            }
            if (optionValuesEqual(val, watchValue, field?.identifier === 'gender')) curValueExist = true;
          });
        }

        if (watchValue && !curValueExist && isCreatable) {
          customValues.push({ value: watchValue, label: watchValue });
        }

        if (customValues.length > 0) {
          arr.push({
            label: translate("state_employee.custom_values", "Benutzerdefinierte Werte"),
            options: customValues,
          });
        }
      } else {
        let curValueExist = false;

        effectiveOptions.forEach((opt) => {
          arr.push({
            value: opt?.value,
            label: opt?.label_translation_key
              ? translate(opt?.label_translation_key, opt?.value)
              : (opt?.label ?? String(opt?.value)),
          });

          if (optionValuesEqual(opt?.value, watchValue, field?.identifier === 'gender')) curValueExist = true;
        });

        if (isCreatable && values.length > 0) {
          values.forEach((val) => {
            const exist = effectiveOptions.find((opt) => optionValuesEqual(opt?.value, val, field?.identifier === 'gender'));
            if (!exist) {
              arr.push({ value: val, label: val });
            }
            if (optionValuesEqual(val, watchValue, field?.identifier === 'gender')) curValueExist = true;
          });
        }

        if (watchValue && !curValueExist && isCreatable) {
          arr.push({ value: watchValue, label: watchValue });
        }
      }
    }

    return arr;
  }, [type, field, values, watchValue, effectiveOptions, isCreatable, translate]);

  const flatSelectOptions = useMemo(() => {
    return selectOptions.flatMap((g) => g?.options || []);
  }, [selectOptions]);

  const getVal = () => {
    if (usesStatePayfactorGrades) {
      const obj = payfactorGradeOptions?.find(o => optionValuesEqual(o.value, watchValue));
      if (obj) return obj.label;
    }

    const selectedOption = field?.grouped_options
      ? flatSelectOptions.find((option) => optionValuesEqual(option.value, watchValue, field?.identifier === 'gender'))
      : selectOptions.find((option) => optionValuesEqual(option.value, watchValue, field?.identifier === 'gender'));
    if (selectedOption) return selectedOption.label;

    if (field?.dynamic_values) {
      const obj = fetchedOptions?.find(o => optionValuesEqual(o.value, watchValue, field?.identifier === 'gender'));
      if (obj) return obj.label;
    }

    return watchValue || '-';
  };

  return (
    readOnly ? <div>
      {getVal()}
    </div> :
      <>
      <div className={`${(field?.suffix && type !== 'date') ? "peb-input-affix" : (type === 'date' ? "input-group" : "")}`}>
        {(["string", "integer"].includes(type) && !effectiveOptions?.length && !isCreatable) && (
          <Input
            type="text"
            error={!!errors?.[field.identifier]}
            placeholder={field?.placeholder_translation_key ? translate(field?.placeholder_translation_key) : ""}
            {...register(field.identifier)}
          />
        )}

        {
          type === 'date' && <>
            <DateInput
              className="w-120"
              error={!!errors?.[field.identifier]}
              placeholder={field?.placeholder_translation_key ? translate(field?.placeholder_translation_key) : ""}
              {...register(field.identifier)}
            />
          </>
        }

        {(type === "float" || type === 'numeric' || (typeof type === "string" && type.includes("decimal"))) && (
          <Input
            type="text"
            error={!!errors?.[field.identifier]}
            placeholder={field?.placeholder_translation_key ? translate(field?.placeholder_translation_key) : ""}
            {...register(field.identifier)}
          />
        )}

        {type === "boolean" && (
          !booleanAsSelect ? <input
            type="checkbox"
            className={`${errors?.[field.identifier] ? "is-invalid" : ""}`}
            checked={isTrue(watchValue)}
            onChange={(e) => setValue(field.identifier, e.target.checked)}
            data-testid={`field-${field.identifier}-checkbox`}
          /> : <NativeSelect data-testid={`field-${field.identifier}-select`}
            className="max-w-100"
            error={!!errors?.[field.identifier]}
            value={String(isTrue(watchValue))}
            onChange={(e) => setValue(field.identifier, e.target.value === "true")}
          >
            <option value="true">{translate('form.yes', 'Ja')}</option>
            <option value="false">{translate('form.no', 'Nein')}</option>
          </NativeSelect>
        )}

        {(((type === "select") || effectiveOptions?.length > 0 || isCreatable) && type !== "radio") && (
          <Controller
            name={field.identifier}
            control={control}
            render={({ field: f }) =>
              isCreatable ? (
                <div style={{ display: 'contents' }} data-testid="employee-data-value-select">
                  <Select
                    as={Creatable}
                    {...f}
                    options={selectOptions}
                    error={!!errors?.[field.identifier]}
                    className={field?.grouped_options ? "peb-rs--grouped" : undefined}
                    onChange={(opt) => {
                      const value = opt?.value;
                      f.onChange(field?.identifier === 'gender' && typeof value === 'string' ? (value.trim() || null) : value);
                    }}
                    value={
                      field?.grouped_options
                        ? (flatSelectOptions.find((o) => optionValuesEqual(o.value, watchValue, field?.identifier === 'gender')) || null)
                        : (selectOptions.find((o) => optionValuesEqual(o.value, watchValue, field?.identifier === 'gender')) || null)
                    }
                    isClearable
                    placeholder={translate('form.select_placeholder')}
                    noOptionsMessage={() => translate("form.no_options_createable")}
                    formatCreateLabel={(val) => translate("form.createable_label") + ` "${val}"`}
                  />
                </div>
              ) : (
                <div style={{ display: 'contents' }} data-testid="employee-data-value-select">
                  <Select
                    {...f}
                    error={!!errors?.[field.identifier]}
                    className={field?.grouped_options ? "peb-rs--grouped" : undefined}
                    options={selectOptions}
                    value={
                      field?.grouped_options
                        ? (flatSelectOptions.find((o) => optionValuesEqual(o.value, watchValue, field?.identifier === 'gender')) || null)
                        : (selectOptions.find((o) => optionValuesEqual(o.value, watchValue, field?.identifier === 'gender')) || null)
                    }
                    onChange={(opt) => f.onChange(opt?.value)}
                    placeholder={translate('form.select_placeholder')}
                    isClearable
                  />
                </div>
              )
            }
          />
        )}

        {
          type === "radio" && <div>
            {
              field?.options?.map((opt, i) => <label key={i} className="d-block">
                <input type="radio" className="v-middle mr-1" {...register(field.identifier)} value={opt?.value} data-testid={`field-${field.identifier}-radio-${opt?.value}`} />
                <span className="break-all v-middle">{editableTranslate(opt?.label_translation_key, opt?.value)}</span>
                {
                  opt?.with_input_text && <Input type="text" className="ml-1 v-middle d-inline-block w-auto" error={!!errors?.[`${field.identifier}_${opt?.value}_input_text`]} {...register(`${field.identifier}_${opt?.value}_input_text`)} disabled={watchValue !== opt?.value} maxLength={255} />
                }
              </label>)
            }
          </div>
        }

        {type === "text" && (
          <EditorInput data-testid={`field-${field.identifier}-input`}
            maxLength={field?.constraints?.max_length || 255}
            threshold={field?.constraints?.max_length ? Math.round(parseInt(field?.constraints?.max_length) * 80 / 100) : 200}
            placeholder={field?.placeholder_translation_key ? translate(field?.placeholder_translation_key) : ""}
            className={`max-height-200 ${errors?.[field.identifier] ? "is-invalid-quill" : ""}`}
            value={watchValue}
            onChangeCallback={(v) => setValue(field.identifier, v, { shouldDirty: true, shouldValidate: true })}
          />
        )}

        {(field?.suffix && type !== 'date') && (
          <span className="peb-input-affix__unit">{field?.suffix}</span>
        )}
      </div>
      {errors?.[field.identifier] && <div className="peb-field__error">{errors[field.identifier].message}</div>}
      </>
  );
}

function StateEmployeeField({ field, employee, form, forceReadOnly, forEvaluator = true, hasLayoutNodeFromSection = null, singleField = false, booleanAsSelect = false }) {
  const { translate, editableTranslate } = useTranslation();
  const userRoles = useSelector(getRoles);

  const dispatch = useDispatch();

  const regularEvaluationLevels = useSelector(getRegularEvaluationLevels);

  const generalSettings = useSelector(getGeneralSettings);

  const readOnly = useMemo(() => {
    const canWrite = field?.can_write_roles && field?.can_write_roles.some((r) => userRoles.includes(r));
    return (
      field?.readonly ||
      !canWrite ||
      (forEvaluator && !field?.can_write_roles.includes("evaluator")) ||
      (!forEvaluator && !field?.can_write_roles.includes("application-admin") && !field?.can_write_roles.includes("hr")) ||
      forceReadOnly ||
      (field?.identifier === 'previous_evaluation_results' && !empty(employee?.previous_evaluation_results?.period))
    );
  }, [field, userRoles, forceReadOnly, forEvaluator]);

  const hasTextArea = useMemo(() => {
    if (field.field_type === "compound") {
      return field.components.find((c) => c?.input_type === "text");
    }
    return false;
  }, [field]);

  const checkboxLabelKey = useMemo(() => field?.identifier + "_checkbox", [field]);

  const resultAsNumeric = useMemo(() => {
    if (!generalSettings) return false;

    // generalSettings is an array of object, search the key 'balanced_scorecard_result_format'
    const setting = generalSettings.find(s => s.key === 'balanced_scorecard_result_format');
    if (setting && setting.value === 'numeric') {
      return true;
    }

    return false;
  }, [generalSettings]);

  const workingHoursAsPtFt = useMemo(() => {
    if (!generalSettings) return true;

    const setting = generalSettings.find(s => s.key === 'working_hours_display');
    if (setting && setting.value === 'parttime_fulltime') return true;
    return false;
  }, [generalSettings]);

  const { watch, setValue, errors } = form;
  const checkboxLabel = watch(checkboxLabelKey);
  const usesStatePayfactorGrades = useMemo(() => STATE_PAYFACTOR_FIELDS.includes(field?.identifier), [field?.identifier]);

  const getEmployeeInformationMeta = (identifier) => {
    return employee?.employeeInformation?.data?.find((item) => item?.key === identifier) || null;
  };

  const getLabeledValue = (value) => {
    if (empty(value)) return '-';

    let tempValue = value;
    if (typeof tempValue === 'string') tempValue = parseFloat(formatNumberTo(tempValue, '.'));

    if (resultAsNumeric) return formatNumberTo(tempValue);

    for (let i = regularEvaluationLevels.length - 1; i >= 0; i--) {
      const lvl = regularEvaluationLevels[i].level;
      const val = parseFloat(formatNumberTo(regularEvaluationLevels[i].value, '.'));
      if (val % 1 === 0.5) {
        if (tempValue === val) return lvl;
      } else if (Math.abs(val - tempValue) < 0.5) return lvl;
    }

    return '-';
  };

  const getVal = (inputField = field) => {
    let val = "-";

    if (inputField.field_type === "compound") {
      let hasValue = false;
      let firstCheckboxAndFalse = false;

      const arr = inputField.components.map((component, i) => {
        if (component.type === "input") {
          let v = employee?.information?.[component.identifier] ?? "";
          if (component?.stored_in_table === 'employees') {
            v = (employee?.[component.identifier] ?? '');
          }

          if ((component?.input_type === 'integer' || component?.input_type === "float" || component?.input_type === 'numeric' || component?.input_type.includes("decimal")) && v !== '') {
            v = removeTrailingZeros(v);
          }

          if (component?.input_type === "boolean") {
            if (isTrue(v)) {
              v = translate("form.yes", "Ja");
            } else {
              v = null;
              if (i === 0) firstCheckboxAndFalse = true;
            }
          }
          if (!empty(v) && !firstCheckboxAndFalse) hasValue = true;
          return v;
        } else if (component.type === "text") {
          // safe fallback so we don't show translation key
          if (component?.text_translation_key) return translate(component.text_translation_key, component.fallback_text || "");
          return component?.text || component?.fallback_text || "";
        } else if (component.type === 'layout') {
          hasValue = true;
        }
        return "";
      });

      if (hasValue) val = arr.join(" ");
    } else {
      val = employee?.information?.[inputField.identifier];

      if (inputField?.stored_in_table === 'employees') {
        val = employee?.[inputField.identifier];
      }

      if (empty(val)) val = '-';

      if ((inputField?.field_type === 'integer' || inputField?.field_type === "float" || inputField?.field_type === 'numeric' || inputField?.field_type.includes("decimal")) && val !== '-') {
        val = removeTrailingZeros(val);
      }

      if (inputField.field_type === "boolean") {
        val = isTrue(val) ? translate("form.yes", "Ja") : "-";
      }

      if (inputField.field_type === "radio" && val !== '-') {
        const opt = inputField?.options?.find(o => o?.value === val);
        if (opt) {
          val = translate(opt?.label_translation_key, opt?.value);
          if (opt?.with_input_text) {
            const inputIdentifier = `${inputField.identifier}_${opt?.value}_input_text`;
            let inputTextVal = employee?.information?.[inputIdentifier] || "-";
            if (inputField?.stored_in_table === 'employees') {
              inputTextVal = (employee?.[inputIdentifier] || '-');
            }

            val += ': ' + inputTextVal;
          }
        }
      }

      if (inputField?.identifier === 'working_hours' && workingHoursAsPtFt && val !== '-') {
        if (parseFloat(formatNumberTo(val, '.')) >= 100) {
          val = translate('general_settings.balanced_scorecard.working_hours_display.fulltime');
        } else {
          val = translate('general_settings.balanced_scorecard.working_hours_display.parttime');
        }
      } else if (inputField?.suffix && val !== "-") {
        val = val + inputField?.suffix;
      }

      if (inputField.identifier === 'previous_evaluation_results') {
        val = employee?.previous_evaluation_results?.regular_evaluation?.result ? getLabeledValue(employee?.previous_evaluation_results?.regular_evaluation?.result) : '-';
      } else if (!empty(val) && inputField.identifier === 'pay_grade') {
        val = employee?.pay_grade_label || val;
      } else if (!empty(val) && inputField.identifier === 'job_differs_from_classification') {
        const resolvedLabel = getEmployeeInformationMeta(inputField.identifier)?.resolved_label;
        if (resolvedLabel) val = resolvedLabel;
      }
    }

    return val;
  };

  const hasLayoutNode = useMemo(() => {
    if (hasLayoutNodeFromSection !== null) return hasLayoutNodeFromSection;

    return Array.isArray(field?.components) && field.components.some((c) => c?.type === "layout");
  }, [field, hasLayoutNodeFromSection]);

  useEffect(() => {
    if (regularEvaluationLevels?.length < 1) dispatch(fetchRegularEvaluationLevels());
  }, []);

  const renderTextNode = (node) => {
    if (node?.text_translation_key) {
      // IMPORTANT: pass fallback to avoid showing the key string
      return editableTranslate(node.text_translation_key, node?.fallback_text || node?.text || "");
    }
    return node?.text || node?.fallback_text || "";
  };

  const renderCompoundNode = (node, key, isReadOnly = false) => {
    if (!node) return null;

    // ✅ special layout node: row with columns
    if (node.type === "layout" && node.layout === "row") {
      return (
        <div key={key} className="row">
          {(node.columns || []).map((col, i) => {
            const label = col?.label?.translation_key
              ? editableTranslate(col.label.translation_key, col.label.text || "")
              : (col?.label?.text || "");

            return (
              <div key={`${key}-col-${i}`} className={`col-12 mb-3`}>
                <div className="row">
                  {label && <div className="col-6">
                    <label className="peb-field__label break-word mb-1">{label}</label>
                  </div>}
                  <div className="col-6">
                    <div className={`d-flex ${col?.wrap || col?.label?.translation_key === 'state_employee.field.evaluation_period' ? 'flex-wrap gap-1' : ''} align-items-start`}>
                      {
                        isReadOnly ? getVal({ field_type: 'compound', components: col.components }) :
                          (col.components || []).map((child, j) => renderCompoundNode(child, `${key}-${i}-${j}`, forEvaluator ? !child?.can_write_roles?.includes("evaluator") : false))
                      }
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (node.type === "input") {
      return (
        <div
          key={key}
          className={`form-input-wrap ${node?.ui?.class_name || ""}`}
          style={node?.ui?.style}
        >
          <StateEmployeeInputTag field={node} type={node.input_type} form={form} readOnly={isReadOnly} booleanAsSelect={booleanAsSelect} />
        </div>
      );
    }

    if (node.type === "text") {
      return (
        <span
          key={key}
          className={node?.ui?.class_name || ""}
          style={node?.ui?.style}
        >
          {renderTextNode(node)}
        </span>
      );
    }

    return null;
  };

  return <>
    {
      !field?.hide_label && <div className={`${!forEvaluator ? 'mb-2' : (singleField || field?.identifier === 'reason_for_event_based_evaluation' ? 'col-12' : 'col-6')}`}>
        {
          (field?.identifier === 'previous_evaluation_results' && forEvaluator && getVal() !== '-') && <label className="peb-field__label break-word mb-2">
            {editableTranslate('state_employee.field.assessment_taken_during_period')}
          </label>
        }

        {!field?.hide_label && (
          <label className="peb-field__label">
            {field?.checkbox_label && (
              <input
                type="checkbox"
                className="mr-2 d-inline-flex v-middle"
                checked={isTrue(checkboxLabel)}
                onChange={(e) => setValue(checkboxLabelKey, e.target.checked)}
                disabled={readOnly}
                data-testid={`field-${field.identifier}-checkbox-label`}
              />
            )}
            <span className={"v-middle break-word"}>{field?.custom_name || editableTranslate(field.translation_key, field.name)}
              {
                (field?.identifier === 'job_differs_from_classification' && field?.custom_name) && <i className="bi bi-info-circle text-normal ml-1" data-tooltip-id={`state-employee-field-${field?.identifier}-tooltip`} data-tooltip-content={translate(field.translation_key, field.name)}></i>
              }

              <ReactTooltip id={`state-employee-field-${field?.identifier}-tooltip`} place="bottom" />
            </span>
          </label>
        )}
      </div>
    }
    <div data-testid={`field-${field.identifier}`} className={`${!forEvaluator ? 'mb-3' : (field?.hide_label || singleField || field?.identifier === 'reason_for_event_based_evaluation' ? 'col-12' : 'col-6')}`}>
      {
        (field?.identifier === 'previous_evaluation_results' && forEvaluator && getVal() !== '-') && <label className="peb-field__label break-word invisible mb-2">{editableTranslate('state_employee.field.assessment_taken_during_period')}</label>
      }
      {(!field?.checkbox_label || isTrue(checkboxLabel)) && (
        <>
          {readOnly ? (
            field.field_type === "compound" && hasLayoutNode ? <div>
              {field.components.map((c, i) => renderCompoundNode(c, `cmp-${i}`, true))}
            </div> :
              (
                (field?.dynamic_values || usesStatePayfactorGrades) ? <StateEmployeeInputTag field={field} type={field.field_type} form={form} regularEvaluationLevels={regularEvaluationLevels} readOnly={true} booleanAsSelect={booleanAsSelect} />
                  : <div
                    className="form-control-plaintext p-0 break-word"
                    dangerouslySetInnerHTML={{ __html: sanitize(employee && !empty(getVal()) ? String(getVal()) : "-") }}
                  />
              )
          ) : (
            <>
              {field.field_type === "compound" ? (
                // ✅ Keep legacy compound behavior, only use layout renderer if layout node exists
                hasLayoutNode ? (
                  <div>
                    {field.components.map((c, i) => renderCompoundNode(c, `cmp-${i}`))}
                  </div>
                ) : (
                  <div className={`d-flex ${(!field?.ui?.single_line || !forEvaluator) ? "flex-wrap" : ""}`}>
                    {field.components.map((component, i) => (
                      <div
                        key={i}
                        className={`${field.components.length > 2 || component?.input_type === "boolean" ? "" : "flex-fill"} ${i === field.components.length - 1 && component?.input_type === "text" ? "flex-fill mw-100" : ""
                          } ${component.type === "text" && !hasTextArea ? "" : ""} ${component?.full_width ? "w-100" : ""}`}
                      >
                        {component.type === "input" && (
                          <div
                            className={`${component?.input_type !== 'text' ? 'form-input-wrap' : ''} ${component?.ui?.class_name || ""}`}
                            style={component?.ui?.style}
                          >
                            <StateEmployeeInputTag field={component} type={component.input_type} form={form} booleanAsSelect={booleanAsSelect} />
                          </div>
                        )}

                        {component.type === "text" && (
                          <span className={`${field?.ui?.single_line ? "text-nowrap" : ""} ${component?.ui?.class_name || ""}`} style={component?.ui?.style}>
                            {component?.text_translation_key
                              ? editableTranslate(component.text_translation_key, component.fallback_text || component.text || "")
                              : (component?.text || component?.fallback_text || "")}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )
              ) : (
                // ✅ normal field: apply ui wrapper (width/class/style)
                <div className={`${(forEvaluator && field?.field_type !== 'radio') ? 'form-input-wrap' : ''} ${field?.ui?.class_name || ""}`} style={field?.ui?.style}>
                  <StateEmployeeInputTag field={field} type={field.field_type} form={form} regularEvaluationLevels={regularEvaluationLevels} booleanAsSelect={booleanAsSelect} />
                </div>
              )}
            </>
          )}
        </>
      )}

      {field?.checkbox_label && errors?.[checkboxLabelKey] && (
        <div className="invalid-feedback d-block">{errors[checkboxLabelKey].message}</div>
      )}
    </div>
  </>;
}

function StateEmployeeSection({ section, employee, form, forceReadOnly, setCountSectionLoaded = null }) {
  const { translate, editableTranslate } = useTranslation();
  const { watch } = form;
  const allValues = watch();

  const filteredFields = useMemo(() => {
    return section.fields.filter((f) => {
      let showConditional = true;

      if (f?.show_conditional) {
        const val = allValues?.[f?.show_conditional?.identifier] || employee?.information?.[f?.show_conditional?.identifier];

        if (typeof f?.show_conditional?.equals === "boolean") {
          showConditional = f?.show_conditional?.equals === isTrue(val);
        } else if (f?.show_conditional?.equals === null) {
          showConditional = empty(val);
        } else {
          showConditional = val === f?.show_conditional?.equals;
        }
      }

      return f.is_active && !f?.hide_for_evaluator && showConditional;
    });
  }, [section, allValues, employee]);

  const hasLayoutNode = (field) => {
    return Array.isArray(field?.components) && field.components.some((c) => c?.type === "layout");
  };

  useEffect(() => {
    if (setCountSectionLoaded) setCountSectionLoaded(c => c + 1);
  }, [setCountSectionLoaded]);

  if (!section.is_active || filteredFields.length < 1) return <></>;

  return (
    <div className="col-6 mb-4 col-compact">
      <h5 className="col-compact-header break-word">
        {editableTranslate(section.translation_key, section.name)}{" "}
        {section?.info_tooltip_translation_key && (
          <i
            className="bi bi-info-circle"
            data-tooltip-id="state-employee-section-tooltip"
            data-tooltip-content={translate(section?.info_tooltip_translation_key)}
          ></i>
        )}
      </h5>

      <div className="gray-box-2 px-3 pt-3 pb-2 mt-2">
        {filteredFields.map((field, i) => {
          const defaultCol = filteredFields.length > 1 ? "col-xl-6 col-xxl-4" : "col";
          const colClass = field?.ui?.col_class || defaultCol;

          return (
            <div key={i} className={`row ${i === filteredFields.length - 1 || (field?.field_type === "compound" && hasLayoutNode(field)) ? '' : 'mb-3'}`}>
              {field?.notes_prefix_text_translation_key && (
                <div className="col-12 mb-2">
                  <span>
                    <strong>{editableTranslate("state_employee.notes.attention")}</strong>{" "}
                    {editableTranslate(field?.notes_prefix_text_translation_key)}
                  </span>
                </div>
              )}

              <StateEmployeeField field={field} employee={employee} form={form} forceReadOnly={forceReadOnly} hasLayoutNodeFromSection={hasLayoutNode(field)} singleField={filteredFields.length === 1} />
              {/* <div className={`${colClass}`}>
              </div> */}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getYupValidation(identifier, type, label, translate, isRequired = false, constraints = null, whenKey = null, whenValue = null, storedInTable = null) {
  let schema;

  if (["string", "select", "text"].includes(type)) {
    schema = yup.string()
      .test(
        "max-length",
        translate('form.max_length_x').replace('$X', constraints?.max_length || 255),
        (value) => {
          if (!value) return true;
          const parsedValue = (type === 'text' ? getTextFromHtml(value) : value);
          return parsedValue.length <= (constraints?.max_length || 255);
        }
      );
  } else if (type === "integer") {
    schema = yup.number().transform((v, ov) => (ov === "" || ov === null || ov === undefined ? null : v)).integer();
    if (!empty(constraints?.min)) schema = schema.min(constraints?.min, translate("form.min_value_x").replace('$X', constraints?.min));
    if (!empty(constraints?.max)) schema = schema.max(constraints?.max, translate("form.max_value_x").replace('$X', constraints?.max));
  } else if (type === "float" || type === "numeric" || (typeof type === "string" && type.includes("decimal"))) {
    schema = yup.string()
      .test("is-decimal", translate("form.valid_format_decimal_3"), (val) => {
        if (empty(val)) return true;
        return val.toString().match(/^\d*(,{1}(\d{1,3}))?$/);
      })
      .test("min-value", constraints?.min ? translate("form.min_value_x").replace('$X', constraints?.min) : '', (val) => {
        if (empty(val) || empty(constraints?.min)) return true;
        const numeric = parseFloat(formatNumberTo(val, '.'));
        return numeric >= parseFloat(formatNumberTo(constraints?.min, '.'));
      })
      .test("max-value", constraints?.max ? translate("form.max_value_x").replace('$X', constraints?.max) : '', (val) => {
        if (empty(val) || empty(constraints?.max)) return true;
        const numeric = parseFloat(formatNumberTo(val, '.'));
        return numeric <= parseFloat(formatNumberTo(constraints?.max, '.'));
      });
  } else if (type === "boolean") {
    schema = yup.mixed().transform((val) => isTrue(val));
  } else if (type === "date") {
    schema = yup.string()
      .test("valid-date", translate("form.valid_format_date"), (val) => {
        if (!val) return true;
        return dayjs(val, "DD.MM.YYYY", true).isValid();
      })
      .test("valid-to-date", translate("form.invalid_end_date"), (val, ctx) => {
        if (!val) return true;
        else {
          const toDate = dayjs(val, 'DD.MM.YYYY');
          if (identifier && identifier.endsWith('_to')) {
            const fromIdentifier = identifier.replace('_to', '_from');
            if (toDate.isValid() && !empty(ctx?.parent?.[fromIdentifier]) && toDate.isBefore(dayjs(ctx?.parent?.[fromIdentifier], 'DD.MM.YYYY'))) return false;
          }
          return true;
        }
      });
  } else {
    schema = yup.string();
  }

  schema = schema.label(label);

  const applyTypeSpecificRequired = (s) => {
    if (type === "boolean") {
      return s.oneOf([true], translate("form.checkbox_required")).required(translate("form.checkbox_required"));
    } else if (type === "text") {
      return s.test(
        "text-required",
        translate('form.field_required'),
        (value) => !empty(getTextFromHtml(value)));
    }
    return s.required();
  };

  if (whenKey) {
    schema = schema.when(whenKey, {
      is: (v) => (typeof v === "boolean" ? isTrue(v) === whenValue : v === whenValue),
      then: (s) => (isRequired ? applyTypeSpecificRequired(s) : s.notRequired().nullable()),
      otherwise: (s) => s.notRequired().nullable(),
    });
  } else {
    schema = isRequired ? applyTypeSpecificRequired(schema) : schema.notRequired().nullable();
  }

  const meta = { type };
  if (storedInTable) meta.stored_in_table = storedInTable;
  schema = schema.meta(meta)

  return schema;
}

function stateEmployeeValidationObj(sections, userRoles, translate, forEvaluator = false) {
  const obj = {};
  if (sections) {
    for (const section of sections) {
      const fields = section.fields.filter((f) => f.is_active);
      for (const field of fields) {
        const canWrite = field?.can_write_roles && field?.can_write_roles.some((r) => userRoles.includes(r));
        const readOnly =
          field?.readonly ||
          !canWrite ||
          (forEvaluator && !field?.can_write_roles.includes("evaluator")) ||
          (!forEvaluator && !field?.can_write_roles.includes("application-admin") && !field?.can_write_roles.includes("hr"));

        let isRequired = field?.is_required || false;
        if (readOnly || (forEvaluator && field?.hide_for_evaluator)) isRequired = false;
        else if (!readOnly && forEvaluator && field?.is_required_evaluator) isRequired = true;

        if (field?.checkbox_label) {
          obj[field?.identifier + "_checkbox"] = getYupValidation(
            field?.identifier,
            "boolean",
            field?.custom_name || translate(field.translation_key, field.name),
            translate,
            isRequired,
            field?.constraints,
            field?.show_conditional?.identifier,
            field?.show_conditional?.equals,
            field?.stored_in_table
          );
        }

        if (field.field_type === "compound") {
          // Support legacy compound inputs for validation (layout nodes don't add new inputs unless nested inputs exist)
          for (const comp of field.components) {
            if (comp.type === "input") {
              obj[comp.identifier] = getYupValidation(
                comp?.identifier,
                comp.input_type,
                field?.custom_name || translate(field.translation_key, field.name),
                translate,
                isRequired,
                field?.constraints,
                field?.show_conditional?.identifier,
                field?.show_conditional?.equals,
                field?.stored_in_table
              );
            }
            if (comp.type === "layout" && comp.layout === "row") {
              for (const col of comp.columns || []) {
                for (const child of col.components || []) {
                  if (child.type === "input") {
                    let childRequired = isRequired;
                    if (!readOnly && forEvaluator && child?.is_required_evaluator) childRequired = true;

                    obj[child.identifier] = getYupValidation(
                      child?.identifier,
                      child.input_type,
                      translate(col?.label?.translation_key || field?.translation_key, col?.label?.text || field?.name),
                      translate,
                      childRequired,
                      field?.constraints,
                      field?.show_conditional?.identifier,
                      field?.show_conditional?.equals,
                      field?.stored_in_table
                    );
                  }
                }
              }
            }
          }
        } else {
          obj[field.identifier] = getYupValidation(
            field?.identifier,
            field.field_type,
            field?.custom_name || translate(field.translation_key, field.name),
            translate,
            isRequired,
            field?.constraints,
            field?.show_conditional?.identifier,
            field?.show_conditional?.equals,
            field?.stored_in_table
          );
        }

        if (field?.options) {
          for (const opt of field?.options) {
            if (opt?.with_input_text) {
              const optInputIdentifier = `${field.identifier}_${opt?.value}_input_text`;
              obj[optInputIdentifier] = getYupValidation(
                optInputIdentifier,
                'string',
                translate(opt?.label_translation_key ?? field.translation_key, field.name),
                translate,
                true,
                null,
                field.identifier,
                opt?.value,
                field?.stored_in_table
              );
            }
          }
        }
      }
    }
  }
  return obj;
}

export default function StateEmployeeDataMode({
  employee,
  setStateEmployeeDirty,
  fetchEmployee,
  forceReadOnly = false,
  callback = null,
  settingIdentifier = null,
  scorecardHashId = null,
  persistToScorecardContext = false,
}) {
  const dispatch = useDispatch();

  const lang = useSelector(getLang);

  const { translate, editableTranslate } = useTranslation();

  const allFields = useSelector(getAllFields);
  const fieldCustomNames = useSelector(getAllCustomNames);

  const sections = useSelector(getStateEmployeeSections);
  const userRoles = useSelector(getRoles);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const compactRowRef = useRef();

  const [countSectionLoaded, setCountSectionLoaded] = useState(0);

  useEffect(() => {
    dispatch(fetchStateEmployeeFields());
  }, [dispatch]);

  const filteredSections = useMemo(() => {
    if (!settingIdentifier) return sections;

    const tempArr = [];
    if (sections) {
      for (const section of sections) {
        const fields = section?.fields?.filter(f => {
          if (f.hasOwnProperty('applicable_settings')) {
            if (f.applicable_settings.includes(settingIdentifier)) return true;
            return false;
          }
          return true;
        });

        if (fields.length > 0) {
          tempArr.push({
            ...section,
            fields
          });
        }
      }

      const newArr = structuredClone(tempArr);

      if (!allFields) return newArr;

      const enabledFields = allFields.filter(f => f?.enabled).map(f => f.translation_key);
      const enabledFieldSlugs = allFields.filter(f => f?.enabled).map(f => f.slug);
      const canonicalGender = allFields.find((field) => field?.slug === 'gender' && field?.field_type === 'fixed');
      const applyCanonicalMetadata = (field) => {
        if (field?.identifier !== 'gender' || !canonicalGender) return field;

        field.stored_in_table = 'employees';
        field.is_createable = true;
        field.dynamic_values = true;
        field.options = (canonicalGender.accepted_values || []).map((value) => ({
          value,
          label: value,
          label_translation_key: `field.gender.value.${String(value).toLocaleLowerCase()}`,
        }));
        return field;
      };

      for (const section of newArr) {
        section.fields = section?.fields?.filter(field => {
          if (field?.field_type === 'compound') {
            field.components = field?.components?.map(component => {
              if (component?.type === 'layout') {
                component.columns = component?.columns?.map(col => {
                  col.components = col?.components?.filter(component2 => {
                    applyCanonicalMetadata(component2);
                    if (component2?.type === 'input' && fieldCustomNames && fieldCustomNames.hasOwnProperty(component2?.translation_key)) {
                      component2.custom_name = fieldCustomNames[component2?.translation_key];
                    }

                    return (
                      component2?.type !== 'input' ||
                      !component2?.is_importable ||
                      enabledFields.includes(component2?.translation_key) ||
                      enabledFieldSlugs.includes(component2?.identifier)
                    );
                  });
                  if (col.components.length === 0) return null;
                  return col;
                }).filter(Boolean);
                if (component.columns.length === 0) return null;
                return component;
              } else if (fieldCustomNames && fieldCustomNames.hasOwnProperty(component?.translation_key)) {
                component.custom_name = fieldCustomNames[component?.translation_key];
              }

              applyCanonicalMetadata(component);

              return (!component?.is_importable || enabledFields.includes(component?.translation_key) || enabledFieldSlugs.includes(component?.identifier)) ? component : null;
            }).filter(Boolean);

            return true;
          } else if (fieldCustomNames && fieldCustomNames.hasOwnProperty(field?.translation_key)) {
            field.custom_name = fieldCustomNames[field?.translation_key];
          }

          applyCanonicalMetadata(field);

          return !field?.is_importable || enabledFields.includes(field?.translation_key) || enabledFieldSlugs.includes(field?.identifier);
        });
      }

      return newArr;
    }
    return [];
  }, [sections, settingIdentifier, allFields]);

  const alignCompactRow = (mutations = null) => {
    const row = compactRowRef?.current;
    const cols = row.querySelectorAll('.col-compact');

    for (let i = 0; i < cols.length; i += 2) {
      const col1 = cols[i];
      const col2 = cols[i + 1];

      if (!col1 || !col2) continue;

      const leftBoxs = col1.querySelectorAll('.gray-box-2');
      const rightBoxs = col2.querySelectorAll('.gray-box-2');

      if (leftBoxs.length < 1 || rightBoxs.length < 1) continue;

      const tempMinL = leftBoxs[0].style.minHeight;
      leftBoxs[0].style.minHeight = 'unset';
      let leftHeight = leftBoxs[0].offsetHeight;
      leftBoxs[0].style.minHeight = tempMinL;

      const tempMinR = rightBoxs[0].style.minHeight;
      rightBoxs[0].style.minHeight = 'unset';
      let rightHeight = rightBoxs[0].offsetHeight;
      rightBoxs[0].style.minHeight = tempMinR;

      if (leftBoxs.length > 1) {
        leftHeight = col1.offsetHeight - (col1.querySelector('.col-compact-header')?.offsetHeight || 0) - 7;
      }
      if (rightBoxs.length > 1) {
        rightHeight = col2.offsetHeight - (col2.querySelector('.col-compact-header')?.offsetHeight || 0) - 7;
      }

      if (col1.querySelector('.div-html-expanded') || col2.querySelector('.div-html-expanded')) continue;

      const maxHeight = Math.max(leftHeight, rightHeight);

      if (leftBoxs.length > 1) {
        let diff = maxHeight - leftHeight;
        let lHeight = Math.min(maxHeight, (leftBoxs[leftBoxs.length - 1].offsetHeight + diff));

        if (col1.querySelector('.div-html')) {
          leftBoxs[leftBoxs.length - 1].style.minHeight = lHeight + 'px';
        } else {
          leftBoxs[leftBoxs.length - 1].style.minHeight = lHeight + 'px';
        }
      } else {
        if (col1.querySelector('.div-html')) {
          leftBoxs[0].style.minHeight = maxHeight + 'px';
        } else {
          leftBoxs[0].style.minHeight = maxHeight + 'px';
        }
      }

      if (rightBoxs.length > 1) {
        let diff = maxHeight - rightHeight;
        let rHeight = Math.min(maxHeight, (rightBoxs[rightBoxs.length - 1].offsetHeight + diff));

        if (col2.querySelector('.div-html')) {
          rightBoxs[rightBoxs.length - 1].style.minHeight = rHeight + 'px';
        } else {
          rightBoxs[rightBoxs.length - 1].style.minHeight = rHeight + 'px';
        }
      } else {
        if (col2.querySelector('.div-html')) {
          rightBoxs[0].style.minHeight = maxHeight + 'px';
        } else {
          rightBoxs[0].style.minHeight = maxHeight + 'px';
        }
      }
    }
  };

  useEffect(() => {
    if (!filteredSections || !compactRowRef?.current) return;

    const observer = new MutationObserver(alignCompactRow);

    observer.observe(compactRowRef.current, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, [filteredSections, compactRowRef]);

  useEffect(() => {
    alignCompactRow();
  }, [countSectionLoaded]);

  useEffect(() => {
    setCountSectionLoaded(c => c + 1);
  }, [employee]);

  const stateEmployeeYupObj = useMemo(() => {
    return stateEmployeeValidationObj(filteredSections, userRoles, translate, true);
  }, [filteredSections, userRoles, translate]);

  const schema = useMemo(() => {
    return yup.object(stateEmployeeYupObj);
  }, [stateEmployeeYupObj]);

  const { register, reset, watch, setValue, control, handleSubmit, formState: { errors, isDirty } } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  useEffect(() => {
    setStateEmployeeDirty(isDirty);
  }, [isDirty, setStateEmployeeDirty]);

  useEffect(() => () => setStateEmployeeDirty(false), [setStateEmployeeDirty]);

  useEffect(() => {
    const obj = {};

    if (employee && schema) {
      for (const key in stateEmployeeYupObj) {
        const meta = stateEmployeeYupObj[key].describe().meta;
        if (meta?.stored_in_table === 'employees') {
          obj[key] = employee?.[key];
        } else {
          obj[key] = employee.information[key] ?? "";
        }

        if (meta?.type === 'integer' && obj[key]) {
          obj[key] = removeTrailingZeros(obj[key]);
        }
      }

      obj.previous_evaluation_results = employee?.previous_evaluation_results?.regular_evaluation?.result || null;
      if (obj.previous_evaluation_results) {
        obj.previous_evaluation_results = removeTrailingZeros(obj.previous_evaluation_results);
      }
    }

    reset(obj);
  }, [employee, schema, reset]);

  const handleSave = async (data) => {
    if (!employee && forceReadOnly) return;

    setError(null);
    setLoading(true);

    const formData = { employee_information: [] };

    for (const key in data) {
      if (schema.fields[key]?.describe().meta?.stored_in_table === 'employees' || key === 'previous_evaluation_results') {
        formData[key] = data[key];
      } else {
        formData.employee_information.push({ key, value: data[key] });
      }
    }

    try {
      const endpoint = persistToScorecardContext && scorecardHashId
        ? `/employee-balanced-scorecards/${scorecardHashId}/state-employee-data`
        : `/employees/${employee.hash_id}/state-employee-data`;

      const { status } = await api.put(endpoint, formData);
      if (status === 200) {
        if (fetchEmployee) fetchEmployee();
        if (callback) callback();
        toast.success(translate("form.success.saved"));
      }
    } catch (err) {
      setError(errorMessage(err, "save state employee data"));
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  return (
    <form className="mt-4" onSubmit={handleSubmit(handleSave)} data-testid="employee-data-section">
      <ErrorAlert error={error} onClose={() => setError(null)} />

      {
        filteredSections && <div className="row" ref={compactRowRef}>
          {
            filteredSections.map((section, i) => (
              <StateEmployeeSection
                key={i}
                section={section}
                employee={employee}
                form={{ register, watch, setValue, errors, control }}
                forceReadOnly={forceReadOnly}
                setCountSectionLoaded={setCountSectionLoaded}
              />
            ))
          }
        </div>
      }

      {!forceReadOnly && (
        <div className="text-right">
          <Button type="submit" variant="primary" loading={loading} data-testid="save-button">{editableTranslate("button.save")}</Button>
        </div>
      )}

      <ReactTooltip id="state-employee-section-tooltip" place="bottom" />
    </form>
  );
}

export { StateEmployeeSection, StateEmployeeField, stateEmployeeValidationObj };
